import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  LocationOn,
  Phone,
  Email,
  Person,
  Store,
  Inventory,
  TrendingUp,
  People,
  AttachMoney
} from '@mui/icons-material';
import api from '../services/api';
import LocationForm from '../components/LocationForm';

const LocationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [location, setLocation] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState(0);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadLocationData();
  }, [id]);

  const loadLocationData = async () => {
    setLoading(true);
    try {
      const [locationResponse, statsResponse] = await Promise.all([
        api.get(`/locations/${id}`),
        api.get(`/locations/${id}/stats`)
      ]);

      setLocation(locationResponse.data.location);
      setStats(statsResponse.data.stats);
    } catch (error) {
      console.error('Error loading location data:', error);
      showSnackbar('Failed to load location details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setOpenEditModal(true);
  };

  const handleSuccess = () => {
    loadLocationData();
    showSnackbar('Location updated successfully', 'success');
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!location) {
    return (
      <Box>
        <Alert severity="error">Location not found</Alert>
        <Button onClick={() => navigate('/locations')} sx={{ mt: 2 }}>
          Back to Locations
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/locations')}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" gutterBottom>
              {location.name}
            </Typography>
            <Chip
              label={location.status === 'active' ? 'Active' : 'Inactive'}
              color={location.status === 'active' ? 'success' : 'default'}
              size="small"
            />
          </Box>
        </Box>
        <Button variant="contained" startIcon={<Edit />} onClick={handleEdit}>
          Edit Location
        </Button>
      </Box>

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Location Information
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box display="flex" alignItems="center" mb={2}>
              <LocationOn color="action" sx={{ mr: 1 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Address
                </Typography>
                <Typography variant="body2">{location.full_address}</Typography>
              </Box>
            </Box>

            {location.phone && (
              <Box display="flex" alignItems="center" mb={2}>
                <Phone color="action" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography variant="body2">{location.phone}</Typography>
                </Box>
              </Box>
            )}

            {location.email && (
              <Box display="flex" alignItems="center" mb={2}>
                <Email color="action" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body2">{location.email}</Typography>
                </Box>
              </Box>
            )}

            {location.manager && (
              <Box display="flex" alignItems="center">
                <Person color="action" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Manager
                  </Typography>
                  <Typography variant="body2">{location.manager.name}</Typography>
                  {location.manager.email && (
                    <Typography variant="caption" color="text.secondary">
                      {location.manager.email}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Store color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">{stats?.sales?.total || 0}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Sales
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    {stats?.sales?.active || 0} active
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Inventory color="secondary" sx={{ mr: 1 }} />
                    <Typography variant="h6">{stats?.inventory?.total || 0}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Inventory Items
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    {stats?.inventory?.available || 0} available
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <TrendingUp color="success" sx={{ mr: 1 }} />
                    <Typography variant="h6">{stats?.leads?.total || 0}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Leads
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    {stats?.leads?.active || 0} active
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <People color="info" sx={{ mr: 1 }} />
                    <Typography variant="h6">{stats?.staff?.count || 0}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Staff Members
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <AttachMoney color="success" sx={{ mr: 1 }} />
                    <Typography variant="h6">
                      ${stats?.revenue?.total?.toLocaleString() || '0'}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Revenue
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tab label="Overview" />
          <Tab label="Sales" />
          <Tab label="Inventory" />
          <Tab label="Staff" />
        </Tabs>

        <Box p={3}>
          {currentTab === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Location Overview
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Performance Summary
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>Total Sales</TableCell>
                          <TableCell align="right">{stats?.sales?.total || 0}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Active Sales</TableCell>
                          <TableCell align="right">{stats?.sales?.active || 0}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Completed Sales</TableCell>
                          <TableCell align="right">{stats?.sales?.completed || 0}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Total Inventory</TableCell>
                          <TableCell align="right">{stats?.inventory?.total || 0}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Items Sold</TableCell>
                          <TableCell align="right">{stats?.inventory?.sold || 0}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>Total Revenue</strong></TableCell>
                          <TableCell align="right">
                            <strong>${stats?.revenue?.total?.toLocaleString() || '0'}</strong>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </Box>
          )}

          {currentTab === 1 && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Sales data for this location will appear here. Navigate to the Sales page and filter by this location to view detailed sales information.
              </Typography>
            </Box>
          )}

          {currentTab === 2 && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Inventory data for this location will appear here. Navigate to the Inventory page and filter by this location to view detailed inventory information.
              </Typography>
            </Box>
          )}

          {currentTab === 3 && (
            <Box>
              {location.staff && location.staff.length > 0 ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Position</TableCell>
                        <TableCell>Primary</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {location.staff.map((member) => (
                        <TableRow key={member.id}>
                          <TableCell>{member.name}</TableCell>
                          <TableCell>{member.position || 'N/A'}</TableCell>
                          <TableCell>
                            {member.is_primary && <Chip label="Primary" color="primary" size="small" />}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No staff assigned to this location yet.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      <LocationForm
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        location={location}
        onSuccess={handleSuccess}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LocationDetail;
